import 'react-datepicker/dist/react-datepicker.css';

import { Button, Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { CalendarIcon } from 'BreetAssets';
import { dateConst } from 'BreetHelpers';
import { addMonths, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

import { CustomHeader } from './CustomHeader';
import type { IDateRangePicker } from './DateRangePicker.helpers';

const DATE_DISPLAY_FORMAT = 'yyyy-MM-dd';
const DATE_PLACEHOLDER = 'yyyy-mm-dd';

const TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour);
const DEFAULT_START_HOUR = 0;
const DEFAULT_END_HOUR = 23;

const formatHourLabel = (hour: number): string => `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? 'AM' : 'PM'}`;

const applyHourToDate = (date: Date, hour: number, boundary: 'start' | 'end'): Date => {
	const next = new Date(date);
	if (boundary === 'end') next.setHours(hour, 59, 59, 999);
	else next.setHours(hour, 0, 0, 0);
	return next;
};

const formatDisplayDate = (date: Date | null, hour: number, showTime: boolean): string => {
	if (!date) return DATE_PLACEHOLDER;
	const day = format(date, DATE_DISPLAY_FORMAT);
	return showTime ? `${day} ${formatHourLabel(hour)}` : day;
};

const DateRangePicker = ({ onApply, onClear, loading, initialStartDate = null, initialEndDate = null }: IDateRangePicker) => {
	const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([initialStartDate, initialEndDate]);
	const [calendarKey, setCalendarKey] = useState(0);
	const [startHour, setStartHour] = useState(DEFAULT_START_HOUR);
	const [endHour, setEndHour] = useState(DEFAULT_END_HOUR);
	const [timeTouched, setTimeTouched] = useState(false);
	const [showTimePanel, setShowTimePanel] = useState(false);
	const [activeBoundary, setActiveBoundary] = useState<'start' | 'end'>('start');

	const [startDate, endDate] = dateRange;

	const dropdownRef = useRef<HTMLDivElement>(null);

	const handleRangeChange = useCallback((update: [Date | null, Date | null]) => {
		const [newStart, newEnd] = update;
		setDateRange(update);
		if (newStart && newEnd) {
			setCalendarKey((k) => k + 1);
		}
	}, []);

	const handleApply = useCallback(() => {
		if (!startDate || !endDate) return;

		const start = applyHourToDate(startDate, startHour, 'start');
		const end = applyHourToDate(endDate, endHour, 'end');

		onApply({
			startDate: start,
			endDate: end,
			utcStartDate: formatInTimeZone(start, 'UTC', dateConst.FULL_DATE_TIME_FORMAT_ISO_Z),
			utcEndDate: formatInTimeZone(end, 'UTC', dateConst.FULL_DATE_TIME_FORMAT_ISO_Z),
		});
	}, [startDate, endDate, startHour, endHour, onApply]);

	const handleClear = useCallback(() => {
		setDateRange([null, null]);
		setStartHour(DEFAULT_START_HOUR);
		setEndHour(DEFAULT_END_HOUR);
		setTimeTouched(false);
		setShowTimePanel(false);
		onClear?.();
	}, [onClear]);

	const toggleTimePanel = useCallback(() => setShowTimePanel((open) => !open), []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setShowTimePanel(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleBoundarySelect = useCallback((boundary: 'start' | 'end') => () => setActiveBoundary(boundary), []);

	const handleTimeSelect = useCallback(
		(hour: number) => () => {
			setTimeTouched(true);
			if (activeBoundary === 'start') setStartHour(hour);
			else setEndHour(hour);
		},
		[activeBoundary]
	);

	const hasRange = Boolean(startDate && endDate);

	const maxSelectableDate = useMemo(() => {
		const today = new Date();
		if (startDate && !endDate) {
			const sixMonthsLater = addMonths(startDate, 6);
			return sixMonthsLater < today ? sixMonthsLater : today;
		}
		return today;
	}, [startDate, endDate]);

	const formatWeekDay = useCallback((day: string) => day.substring(0, 3), []);

	return (
		<div className='bt_dateRangePicker border-dark-navy'>
			<div className='bt_dateRangePicker_header'>
				<div
					className='bt_dateRangePicker_header_timewrap'
					ref={dropdownRef}
				>
					<button
						type='button'
						className={`bt_dateRangePicker_header_timebtn${showTimePanel ? ' open' : ''}`}
						onClick={toggleTimePanel}
					>
						<span className='bt_dateRangePicker_header_timebtn_label'>
							{timeTouched ? `${formatHourLabel(startHour)} – ${formatHourLabel(endHour)}` : 'Time'}
						</span>
						<span className='bt_dateRangePicker_header_timebtn_chevron' />
					</button>

					{showTimePanel && (
						<div className='bt_dateRangePicker_timepanel bg-primary-600 theme-border-none'>
							<div className='bt_dateRangePicker_timepanel_toggle border-bottom-dark-navy'>
								<button
									type='button'
									className={`bt_dateRangePicker_timepanel_tab${activeBoundary === 'start' ? ' active' : ''}`}
									onClick={handleBoundarySelect('start')}
								>
									Start
								</button>
								<button
									type='button'
									className={`bt_dateRangePicker_timepanel_tab${activeBoundary === 'end' ? ' active' : ''}`}
									onClick={handleBoundarySelect('end')}
								>
									End
								</button>
							</div>

							<ul className='bt_dateRangePicker_timepanel_list scrollbar'>
								{TIME_OPTIONS.map((hour) => {
									const selected = activeBoundary === 'start' ? startHour === hour : endHour === hour;

									return (
										<li key={hour}>
											<button
												type='button'
												className={classNameMerger(
													'bt_dateRangePicker_timepanel_option bg-primary-500-hover',
													selected && 'active bg-primary-400'
												)}
												onClick={handleTimeSelect(hour)}
											>
												{formatHourLabel(hour)}
											</button>
										</li>
									);
								})}
							</ul>
						</div>
					)}
				</div>

				<div className='bt_dateRangePicker_header_inputs'>
					<div className='bt_dateRangePicker_header_inputs_field'>
						<Typography
							as='span'
							variant='paragraph_2'
							className='bt_dateRangePicker_header_inputs_value'
						>
							{formatDisplayDate(startDate, startHour, timeTouched)}
						</Typography>
						<span className='bt_dateRangePicker_header_inputs_arrow'>→</span>
						<Typography
							as='span'
							variant='paragraph_2'
							className='bt_dateRangePicker_header_inputs_value'
						>
							{formatDisplayDate(endDate, endHour, timeTouched)}
						</Typography>
						<CalendarIcon className='bt_dateRangePicker_header_inputs_icon svg-light' />
					</div>
				</div>

				<div className='bt_dateRangePicker_header_actions'>
					<Button
						type='button'
						className={classNameMerger('bt_dateRangePicker_applybtn', !hasRange && 'disabled')}
						disabled={!hasRange || loading}
						onClick={handleApply}
					>
						{loading ? (
							<span className='button-spinner bt_dateRangePicker_applybtn_spinner' />
						) : (
							<Typography
								as='span'
								variant='paragraph_2'
								className='bt_dateRangePicker_applybtn_label'
							>
								Apply Filter
							</Typography>
						)}
					</Button>

					<button
						type='button'
						className='bt_dateRangePicker_header_actions_clear'
						onClick={handleClear}
					>
						<Typography
							as='span'
							variant='paragraph_2'
						>
							Clear
						</Typography>
					</button>
				</div>
			</div>

			<div className='bt_dateRangePicker_calendar'>
				<DatePicker
					key={calendarKey}
					onChange={handleRangeChange}
					startDate={startDate ?? undefined}
					endDate={endDate ?? undefined}
					openToDate={startDate ?? undefined}
					maxDate={maxSelectableDate}
					selectsRange
					monthsShown={2}
					inline
					shouldCloseOnSelect={false}
					formatWeekDay={formatWeekDay}
					renderCustomHeader={CustomHeader}
				/>
			</div>
		</div>
	);
};

export default DateRangePicker;
